/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AclModel, L7BackendServiceModel } from '../../../models';
import { AclService } from '../../../services';
import { BaseComponent } from '../../../../../../core/components';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    TransactionService
} from '../../../../../../core/services';
import { Layer4ApiService, Layer7ApiService } from '../../../http';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of, Subscriber } from 'rxjs';

@Component({
    selector: 'app-lb-modals-hanging-backends',
    styleUrls: ['./hangingBackends.modal.component.scss'],
    templateUrl: './hangingBackends.modal.component.html',
})

export class HangingBackendsModalComponent extends BaseComponent {
    /**
     * Reference to the content of this component.
     */
    @ViewChild(TemplateRef) public templateRef: TemplateRef<any>;

    /**
     * External: Incoming hangingBackends
     */
    public hangingBackends: any[] = [];

    /**
     * External: List of servers associated to a backend
     */
    public realServersByBackend: any = [];

    /**
     * External: Call back function in the parent to update the backends on the parent.
     */
    public backendToDelete: (...args: any[]) => void;

    /**
     * Array to store hanging backends
     */
    public hangingBackendRows: any = [];

    /**
     * To store the real server's information.
     */
    public realServersInfo: any = [];

    /**
     * To store the name for the real servers modal.
     */
    public modalName: string = '';

    /**
     * Store of multiple selected rows in the table.
     */
    public selectedRows: any = [];

    /**
     * Sets modal submission state.
     */
    public submissionActive: boolean = false;

    /**
     * To store the progress message.
     */
    public progressMessage: string = '';

    /**
     * Store frontends.
     */
    public frontends: any[] = [];

    /**
     * Store permissions.
     */
    public permissionsLookup: any = {};

    /**
     * Store multi select actions to pass to the table component.
     */
    public multiSelectActions: any[];

    /**
     * Store backends.
     * @private
     */
    private backends: any[] = [];

    /**
     * Store acls.
     * @private
     */
    private acls: AclModel[] = [];

    constructor(
        private aclService: AclService,
        private languageService: LanguageService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private transactionService: TransactionService
    ) {
        super();

        this.setUpPageData();
        this.registerButtons();
        this.setSelectAllActions();
    }

    /**
     * Show usage modal
     */
    public showUsageModal(row: any): void {
        this.modalName = row.name;
        this.realServersInfo = this.realServersByBackend[row.id];
        this.modalService.openModalInParent('BackendRealServersUsageModal');
    }

    /**
     * Triggered when a table row is selected.
     * @param selected
     */
    public onSelect(selected: any): void {
        this.selectedRows = selected;
    }

    /**
     * Create ACL for hanging backend.
     * @param row
     */
    public createACL(row: any): void {
        const redirectUrl: string = '/cluster-configuration/manage-services';
        const redirectText: string = 'manage_services_return';
        this.aclService.addEditAcl(this.frontends, this.backends, this.acls, redirectUrl, redirectText, row.id, null, row);
    }

    /**
     * Set the selected rows to be equal to the total rows
     */
    public selectAllRows(): void {
        this.selectedRows = this.hangingBackendRows;
        // Kick the view
        this.hangingBackendRows = [...this.hangingBackendRows];
    }

    /**
     * Confirm delete of backends.
     * @param backends
     */
    public confirmDeleteBackend(backends: any[] = this.selectedRows): void {
        const message = backends.length > 1
            ? this.languageService.translate('hanging_backend_delete_message_multiple')
            : `${this.languageService.translate('hanging_backend_delete_message')} '${backends[0].name}'?`;

        const confirmationObject = {
            title: 'hanging_backend_delete',
            message: message,
            options: { translateText: false },
            callBack: this.deleteBackendTransaction.bind(this, backends)
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete backend transaction helper function.
     * @param backends
     * @private
     */
    private deleteBackendTransaction(backends: L7BackendServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.deleteBackend(backends)),
                    concatMap(() => this.commitTransaction()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.loadingService.loadingActionDotsEmitter.next(false);
                        this.progressMessage = '';
                        this.modalService.close('backendProgressModal');
                        this.updateView();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'HangingBackendsModalComponent:deleteBackendTransaction',
                            'Error with the transaction',
                            error
                        );
                        this.transactionService.rollbackTransaction();
                        observer.error(error);
                    }
                });
        });
    }

    /**
     * Delete the backend/s
     * @param backends
     * @private
     */
    private deleteBackend(backends: L7BackendServiceModel[]): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            const idsToDelete = this.prepareBackendData(backends);
            const dataToSubmit = from(idsToDelete || []);
            const translation: string = this.languageService.translate('hanging_backend_deleting');
            const ofText: string = this.languageService.translate('of');
            let index: number = 1;
            if (idsToDelete.length > 1) {
                this.modalService.openModalInParent('backendProgressModal');
                this.progressMessage = `${translation} ${index} ${ofText} ${idsToDelete.length}`;
            }
            const call: Observable<any> = dataToSubmit.pipe(
                mergeMap((id: string) => this.deleteHangingBackendHelper(id).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (): void => {
                    if (idsToDelete.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${idsToDelete.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Helper function for deleting.
     * @param id
     * @private
     */
    private deleteHangingBackendHelper(id: string) {
        return new Observable((observer) => {
            const deleteIndex: number = this.hangingBackends.findIndex(item => item.id === id);
            const backend: any = this.hangingBackends[deleteIndex];
            const apiService: any = backend.templateType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;

            this.hangingBackends.splice(deleteIndex, 1);

            apiService.deleteBackend(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Commit transaction.
     * @private
     */
    private commitTransaction(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            'frontends7': this.layer7ApiService.getFrontends(),
            'backends7': this.layer7ApiService.getBackends(),
            'frontends4': this.layer4ApiService.getFrontends(),
            'backends4': this.layer4ApiService.getBackends(),
            'acls4': this.layer7ApiService.getAclRules(),
            'acls7': this.layer4ApiService.getAclRules()
        };
        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.frontends = data['frontends7'].concat(data['frontends4']);
                    this.backends = data['backends7'].concat(data['backends4']);
                    this.acls = data['acls7'].concat(data['acls4']);

                    this.populateHangingBackendRows();
                    if (!this.modalService.isModelOpen('hangingBackendsModal')) {
                        this.modalService.openModalInParent('hangingBackendsModal');
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HangingBackendsModalComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Prepare the backend's data ready for deleting.
     * @param backends
     * @private
     */
    private prepareBackendData(backends: L7BackendServiceModel[]): any {
        const idsToDelete = backends.length === 1
            ? [backends[0].id]
            : this.selectedRows.map((row: L7BackendServiceModel) => row.id);

        if (idsToDelete.length > 1) {
            this.submissionActive = true;
            this.progressMessage = 'Deleting hanging backends';
        }

        return idsToDelete;
    }

    /**
     * Helper function for updating the view.
     * @private
     */
    private updateView(): void {
        if (this.hangingBackends.length) {
            // Updated page and get a new list of backends.
            this.populateHangingBackendRows();
            // Delete the backends on the parent component.
            this.backendToDelete();
            // Reset the selectedRows variable when all selected rows have been deleted.
            this.selectedRows = [];
        } else {
            this.backendToDelete();
            this.modalService.close('hangingBackendsModal');
            this.notifyService.success('hanging_backend_deleted_all', { messageTranslate: true });
        }
    }

    /**
     * Populate the hanging backends array.
     * @private
     */
    private populateHangingBackendRows(): void {
        this.hangingBackendRows = [];
        const realServersById: string[] = Object.keys(this.realServersByBackend) || [];
        for (const backend of this.hangingBackends) {
            if (!realServersById.includes(backend.id)) {
                this.getRealServerByBackendId(backend);
            }

            this.hangingBackendRows.push({
                id: backend.id,
                name: backend.name,
                mode: backend.mode?.toUpperCase() || backend.forwardingMode?.toUpperCase(),
                realServers: backend.ripIds,
                // @ts-ignore
                templateType: backend.templateType
            });
        }

        this.hangingBackendRows = [...this.hangingBackendRows];
    }

    /**
     * Create real server details for each backend
     * @param backend
     * @private
     */
    private getRealServerByBackendId(backend: any): void {
        const backendId = backend.id;
        const apiService: any = backend.templateType === 'layer4'
            ? this.layer4ApiService : this.layer7ApiService;
        apiService.getRealServersByBackendId(backendId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (!this.realServersByBackend[backendId]) {
                        this.realServersByBackend[backendId] = data;
                    }
                }
            });
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'hanging-backends-create-acl': this.getAclPermissions(),
            'hanging-backends-delete-backend': [{ path: '/haproxy/backends/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Get permissions required for creating an ACL.
     * @private
     */
    private getAclPermissions(): any[] {
        return [
            { path: '/fips', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/backends/:id', methods: ['PUT'] },
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['PUT'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Set the select all button.
     * @private
     */
    private setSelectAllActions(): void {
        this.multiSelectActions = [
            {
                buttonText: 'hanging_backend_delete_selected',
                buttonAction: this.confirmDeleteBackend.bind(this),
                buttonDisabled: !this.permissionsLookup['hanging-backends-delete-backend']
            }
        ];
    }
}
